import { useState } from "react";
import { useTranslation } from "@repo/i18n";
import { Button } from "@repo/components";
import { ApiError } from "@repo/api-client";
import type { WalkInRequest } from "@repo/api-client";
import { useWalkIn } from "./queries";
import { liveStyles as s } from "./styles";

/**
 * The shared walk-in control (party size + optional name/phone -> `useWalkIn`), extracted from the door
 * view so both the door AND the live floor add a walk-in through ONE form + mutation. Seats immediately
 * onto capacity (`resourceType: "CAPACITY"`, `seatImmediately: true`), so on success the guest joins the
 * live floor via the same invalidation the door always used. A French phone typed `0612…` is normalized
 * to `+33…` SERVER-side (PhoneNormalizerPort, GU-1) — the client sends the raw string. Targets >= 44px.
 */
export function WalkInForm({
  venueId,
  serviceId,
  prominent = false,
}: {
  venueId: string;
  serviceId: string | undefined;
  /** Render the collapsed trigger as a large solid CTA (used on the live floor). */
  prominent?: boolean;
}) {
  const { t } = useTranslation();
  const walkIn = useWalkIn(venueId);
  const [open, setOpen] = useState(false);
  const [party, setParty] = useState(2);
  const [givenName, setGivenName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!serviceId) {
      setMessage(t("door.walkIn.noService"));
      return;
    }
    const body: WalkInRequest = {
      serviceId,
      startSec: Math.floor(Date.now() / 1000),
      partySize: party,
      guest: {
        givenName: givenName.trim() || "Walk-in",
        familyName: null,
        phone: phone.trim() || null,
        email: null,
      },
      resourceType: "CAPACITY",
      seatImmediately: true,
    };
    walkIn.mutate(body, {
      onSuccess: () => {
        setMessage(t("door.walkIn.created"));
        setGivenName("");
        setPhone("");
        setParty(2);
        setOpen(false);
      },
      onError: (err) => {
        const code =
          err instanceof ApiError
            ? (err.problem as { rejectionCode?: string } | undefined)?.rejectionCode
            : undefined;
        if (code === "GUEST_BLOCKED") setMessage(t("door.walkIn.blocked"));
        else setMessage(t("door.walkIn.failed"));
      },
    });
  };

  if (!open) {
    return (
      <div className={s.walkInBar}>
        {message && <output className={s.walkInMsg}>{message}</output>}
        <Button
          variant="solid"
          size={prominent ? "xl" : "lg"}
          alignSelf="flex-start"
          onClick={() => setOpen(true)}
        >
          {t("door.walkIn.add")}
        </Button>
      </div>
    );
  }

  return (
    <form className={s.walkInForm} onSubmit={submit} aria-label={t("door.walkIn.add")}>
      <div className={s.field}>
        <label htmlFor="wi-party" className={s.fieldLabel}>
          {t("door.walkIn.party")}
        </label>
        <input
          id="wi-party"
          type="number"
          min={1}
          max={500}
          value={party}
          onChange={(e) => setParty(Math.max(1, Number(e.target.value) || 1))}
          className={s.input}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="wi-name" className={s.fieldLabel}>
          {t("door.walkIn.name")}
        </label>
        <input
          id="wi-name"
          type="text"
          value={givenName}
          onChange={(e) => setGivenName(e.target.value)}
          className={s.input}
        />
      </div>
      <div className={s.field}>
        <label htmlFor="wi-phone" className={s.fieldLabel}>
          {t("door.walkIn.phone")}
        </label>
        <input
          id="wi-phone"
          type="tel"
          inputMode="tel"
          placeholder="06 12 34 56 78"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={s.input}
        />
      </div>
      <div className={s.rowActions}>
        <Button type="submit" variant="solid" loading={walkIn.isPending}>
          {t("door.walkIn.seat")}
        </Button>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          {t("door.walkIn.cancel")}
        </Button>
      </div>
      {message && (
        <p role="alert" className={s.walkInMsg}>
          {message}
        </p>
      )}
    </form>
  );
}
